/**
 * Lumeska premium product gallery — progressive enhancement layer.
 * The track is a native scroll-snap row, so swipe works with zero JS.
 * This script only: syncs the dot/arrow controls to scroll position,
 * plays/pauses slide video by visibility, and drives the before/after
 * comparison handle.
 */
(function () {
  const prefersReducedMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const saveDataEnabled = () => 'connection' in navigator && navigator.connection.saveData;

  class PdpGallery {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-pdp-gallery-track]');
      this.slides = Array.from(root.querySelectorAll('[data-pdp-slide]'));
      this.dots = Array.from(root.querySelectorAll('[data-pdp-gallery-dot]'));
      this.prevBtn = root.querySelector('[data-pdp-gallery-prev]');
      this.nextBtn = root.querySelector('[data-pdp-gallery-next]');
      this.reducedMotion = prefersReducedMotion();

      if (!this.track || !this.slides.length) return;

      this.setupActiveTracking();
      this.setupControls();
      this.setupVideos();
      this.setupBeforeAfter();
    }

    setupActiveTracking() {
      if (!('IntersectionObserver' in window)) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const index = this.slides.indexOf(entry.target);
            if (index === -1) return;

            if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
              this.setActiveIndex(index);
            }

            const video = entry.target.querySelector('[data-pdp-video], .pdp-gallery__video');
            if (video) {
              if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
                this.playVideo(video, entry.target);
              } else {
                video.pause();
              }
            }
          });
        },
        { root: this.track, threshold: [0.5, 0.6] }
      );

      this.slides.forEach((slide) => observer.observe(slide));
    }

    setActiveIndex(index) {
      this.dots.forEach((dot, i) => {
        dot.setAttribute('aria-selected', String(i === index));
      });
    }

    setupControls() {
      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', () => this.scrollToIndex(i));
      });

      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this.scrollByOne(-1));
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this.scrollByOne(1));
      }
    }

    scrollToIndex(index) {
      const slide = this.slides[index];
      if (!slide) return;
      this.track.scrollTo({
        left: slide.offsetLeft,
        behavior: this.reducedMotion ? 'auto' : 'smooth',
      });
    }

    scrollByOne(direction) {
      this.track.scrollBy({
        left: direction * this.track.clientWidth,
        behavior: this.reducedMotion ? 'auto' : 'smooth',
      });
    }

    setupVideos() {
      const videoSlides = this.slides.filter((slide) => slide.classList.contains('pdp-gallery__slide--video'));

      videoSlides.forEach((slide) => {
        const video = slide.querySelector('.pdp-gallery__video');
        const toggle = slide.querySelector('[data-pdp-video-toggle]');
        if (!video) return;

        video.muted = true;
        video.defaultMuted = true;

        video.addEventListener('playing', () => slide.classList.add('is-video-playing'));

        if (toggle) {
          toggle.setAttribute('aria-pressed', 'true');
          toggle.addEventListener('click', () => {
            const isPaused = slide.classList.toggle('is-video-paused');
            toggle.setAttribute('aria-pressed', String(!isPaused));
            if (isPaused) {
              video.pause();
            } else {
              video.play().catch(() => {});
            }
          });
        }
      });
    }

    playVideo(video, slide) {
      if (this.reducedMotion || saveDataEnabled()) return;
      if (slide.classList.contains('is-video-paused')) return;
      const playPromise = video.play();
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise.catch(() => {});
      }
    }

    setupBeforeAfter() {
      const baSlides = Array.from(this.root.querySelectorAll('[data-pdp-ba]'));

      baSlides.forEach((ba) => {
        const handle = ba.querySelector('[data-pdp-ba-handle]');
        if (!handle) return;

        const setPosition = (percent) => {
          const clamped = Math.min(100, Math.max(0, percent));
          ba.style.setProperty('--ba-pos', clamped + '%');
          handle.setAttribute('aria-valuenow', String(Math.round(clamped)));
        };

        const percentFromClientX = (clientX) => {
          const bounds = ba.getBoundingClientRect();
          return ((clientX - bounds.left) / bounds.width) * 100;
        };

        let dragging = false;

        const onPointerMove = (event) => {
          if (!dragging) return;
          setPosition(percentFromClientX(event.clientX));
        };

        const onPointerUp = () => {
          dragging = false;
          window.removeEventListener('pointermove', onPointerMove);
          window.removeEventListener('pointerup', onPointerUp);
        };

        handle.addEventListener('pointerdown', (event) => {
          dragging = true;
          setPosition(percentFromClientX(event.clientX));
          window.addEventListener('pointermove', onPointerMove);
          window.addEventListener('pointerup', onPointerUp);
        });

        ba.addEventListener('pointerdown', (event) => {
          if (event.target === handle || handle.contains(event.target)) return;
          setPosition(percentFromClientX(event.clientX));
        });

        handle.addEventListener('keydown', (event) => {
          const current = parseFloat(ba.style.getPropertyValue('--ba-pos')) || 50;
          if (event.key === 'ArrowLeft') {
            setPosition(current - 5);
            event.preventDefault();
          } else if (event.key === 'ArrowRight') {
            setPosition(current + 5);
            event.preventDefault();
          } else if (event.key === 'Home') {
            setPosition(0);
            event.preventDefault();
          } else if (event.key === 'End') {
            setPosition(100);
            event.preventDefault();
          }
        });
      });
    }
  }

  function initAll(root = document) {
    root.querySelectorAll('[data-pdp-gallery]:not([data-pdp-gallery-initialized])').forEach((el) => {
      el.setAttribute('data-pdp-gallery-initialized', 'true');
      new PdpGallery(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  }
})();
